import React, { useState, useEffect } from 'react';
import { Edit2, UserPlus, Trash2, X, Save, RefreshCw, AlertCircle, FileDown, Loader2, CalendarDays, Receipt, ShieldCheck, Check, AlertTriangle } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { UserService, RelazioneGiornalieraService, ConsentService, legalDocTypeLabel } from "../services/apiService";
import { User, UserRole, UserDto } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { generaRiepilogoAdminCollaboratore } from "../services/generaRiepilogoCollaboratore";
import { generaAgendaCollaboratore } from "../services/generaAgendaCollaboratore";
import { getIncaricoStatoBadge, getTurnoStatoBadge } from "../utils/incaricoUtils";
import ResponsiveTable from '../components/ResponsiveTable';
import { localIsoDate } from '../utils/utils';

// Form: UserDto più la password (solo lato frontend)
interface CollaboratorFormData {
    id?: number;
    name: string;
    surname: string;
    email: string;
    role: UserRole;
    password?: string; // Opzionale in modifica
}

const CollaboratoriView: React.FC = () => {
    const navigate = useNavigate();
    // Stato
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Modale CRUD
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Consensi legali del collaboratore aperto (solo ADMIN).
    const { user: authUser } = useAuth();
    const isAdmin = authUser?.role === UserRole.ADMIN;
    const [modalConsents, setModalConsents] = useState<{ pending: any[]; accepted: any[] } | null>(null);

    // Modale riepilogo
    const [riepilogoUser, setRiepilogoUser] = useState<User | null>(null);
    const [riepilogoMonth, setRiepilogoMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [riepilogoLoading, setRiepilogoLoading] = useState(false);

    // Modale agenda
    const [agendaUser, setAgendaUser] = useState<User | null>(null);
    const [agendaData, setAgendaData] = useState<any | null>(null);
    const [agendaLoading, setAgendaLoading] = useState(false);
    const [agendaFrom, setAgendaFrom] = useState(() => {
        const d = new Date(); d.setDate(1);
        return localIsoDate(d);
    });
    const [agendaTo, setAgendaTo] = useState(() => localIsoDate());

    const handleOpenAgenda = (user: User) => {
        setAgendaUser(user);
        setAgendaData(null);
    };
    const handleLoadAgenda = async () => {
        if (!agendaUser) return;
        setAgendaLoading(true);
        try {
            const data = await UserService.getAgenda(agendaUser.id, agendaFrom, agendaTo);
            setAgendaData(data);
        } catch {
            alert('Errore nel caricamento dei dati.');
        } finally {
            setAgendaLoading(false);
        }
    };
    const handleDownloadAgenda = () => {
        if (!agendaData) return;
        generaAgendaCollaboratore(agendaData, agendaFrom, agendaTo);
    };

    const handleDownloadRiepilogo = async () => {
        if (!riepilogoUser) return;
        setRiepilogoLoading(true);
        try {
            const res = await RelazioneGiornalieraService.getAllApprovedByMonth(riepilogoMonth);
            const all: any[] = Array.isArray(res) ? res : res?.data || [];
            const filtered = all.filter((r: any) =>
                r.collaboratore?.id === riepilogoUser.id ||
                r.collaboratoreId === riepilogoUser.id
            );
            const nome = `${riepilogoUser.name} ${riepilogoUser.surname}`.trim();
            generaRiepilogoAdminCollaboratore(filtered, riepilogoMonth, nome);
            setRiepilogoUser(null);
        } catch {
            alert('Errore nel caricamento dei dati. Riprovare.');
        } finally {
            setRiepilogoLoading(false);
        }
    };

    // Form
    const [formData, setFormData] = useState<CollaboratorFormData>({
        name: '',
        surname: '',
        email: '',
        role: UserRole.COLLABORATORE,
        password: ''
    });

    // Caricamento dati
    const fetchUsers = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await UserService.getAll();
            if (response.success) {
                setUsers(response.data);
            } else {
                setError(response.message || "Errore nel caricamento dei dati.");
            }
        } catch (err) {
            console.error("Errore fetch:", err);
            setError("Impossibile contattare il server.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Gestione modale
    const openAddModal = () => {
        setFormData({
            name: '',
            surname: '',
            email: '',
            role: UserRole.COLLABORATORE,
            password: '' // Reset password field
        });
        setIsEditing(false);
        setIsModalOpen(true);
    };

    const openEditModal = (user: User) => {
        setFormData({
            id: user.id,
            name: user.name,
            surname: user.surname,
            email: user.email,
            role: user.role,
            password: '' // Non mostriamo la vecchia password
        });
        setIsEditing(true);
        setIsModalOpen(true);
        // Consensi legali dell'utente (endpoint ADMIN-only).
        setModalConsents(null);
        if (isAdmin && user.id != null) {
            ConsentService.getUserConsents(user.id)
                .then(s => setModalConsents({ pending: s.pending || [], accepted: s.accepted || [] }))
                .catch(() => { /* Non ADMIN o 403: il blocco non viene mostrato */ });
        }
    };

    // Azioni CRUD
    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);

        try {
            // Preparazione DTO. Tariffe, buoni pasto e sedi non si gestiscono da qui e restano
            // fuori dal payload: il backend lascia invariati i campi assenti.
            const payload: any = {
                name: formData.name,
                surname: formData.surname,
                email: formData.email,
                role: formData.role
            };

            // In creazione aggiungiamo la password, se il backend la richiede.
            if (!isEditing && formData.password) {
                payload.password = formData.password;
            }

            let response;
            if (isEditing && formData.id) {
                // Update: nel DTO solo i campi necessari, la password non si cambia da qui.
                response = await UserService.update(formData.id, payload as UserDto);
            } else {
                // Create
                response = await UserService.create(payload as UserDto);
            }

            if (response.success) {
                setIsModalOpen(false);
                fetchUsers(); // Ricarica la lista
            } else {
                alert(`Errore: ${response.message}`);
            }

        } catch (err: any) {
            console.error("Save error:", err);
            alert("Si è verificato un errore durante il salvataggio.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm("Sei sicuro di voler eliminare questo utente? L'operazione è irreversibile: il nome viene ridotto alle iniziali e l'accesso viene bloccato.")) {
            try {
                const response = await UserService.delete(id);
                if (response) {
                    // Rimozione locale, senza ricaricare
                    setUsers(prev => prev.filter(u => u.id !== id));
                } else {
                    alert("Impossibile eliminare l'utente.");
                }
            } catch (err) {
                console.error("Delete error:", err);
                alert("Errore durante l'eliminazione.");
            }
        }
    };

    // Helper UI
    const getRoleLabel = (role: string) => {
        switch (role) {
            case UserRole.ADMIN: return 'Titolare';
            case UserRole.RESPONSABILE: return 'Responsabile';
            case UserRole.COLLABORATORE: return 'Collaboratore';
            default: return role;
        }
    };

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case UserRole.ADMIN: return 'bg-purple-100 text-purple-700 border-purple-200';
            case UserRole.RESPONSABILE: return 'bg-blue-100 text-blue-700 border-blue-200';
            case UserRole.COLLABORATORE: return 'bg-green-100 text-green-700 border-green-200';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Gestione Team</h1>
                    <p className="text-sm text-gray-500 mt-1">Gestisci i permessi e gli accessi dei collaboratori.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {/* "Ore & Spese Collaboratori" sta qui, nella sezione collaboratori. */}
                    <button
                        onClick={() => navigate('/amministrazione/report-mensile')}
                        className="flex items-center gap-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all active:scale-95"
                    >
                        <Receipt size={18} /> Ore &amp; Spese Collaboratori
                    </button>
                    <button
                        onClick={fetchUsers}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Aggiorna lista"
                    >
                        <RefreshCw size={20} className={isLoading ? "animate-spin" : ""} />
                    </button>
                    <button
                        onClick={openAddModal}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-all shadow-md hover:shadow-lg active:scale-95"
                    >
                        <UserPlus size={18} /> Nuovo Utente
                    </button>
                </div>
            </div>

            {/* Error Banner */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-3">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                </div>
            )}

            {/* Tabella */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {isLoading ? (
                    <div className="p-6 space-y-3">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="animate-pulse flex gap-4">
                                <div className="h-4 bg-gray-200 rounded w-32"></div>
                                <div className="h-4 bg-gray-200 rounded w-20"></div>
                                <div className="h-4 bg-gray-200 rounded w-40"></div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <ResponsiveTable
                        data={users}
                        keyExtractor={(u) => u.id}
                        emptyMessage="Nessun collaboratore trovato."
                        columns={[
                            {
                                key: 'utente',
                                label: 'Utente',
                                render: (user) => (
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                                            {user.name.charAt(0)}{user.surname.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">{user.name} {user.surname}</div>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                key: 'ruolo',
                                label: 'Ruolo',
                                render: (user) => (
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                                        {getRoleLabel(user.role)}
                                    </span>
                                ),
                            },
                            {
                                key: 'email',
                                label: 'Email',
                                render: (user) => (
                                    <span className="text-sm text-gray-500">{user.email}</span>
                                ),
                            },
                            {
                                key: 'actions',
                                label: 'Azioni',
                                align: 'right',
                                render: (user) => (
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => handleOpenAgenda(user)}
                                            className="text-gray-400 hover:text-blue-600 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                                            title="Agenda attività"
                                        >
                                            <CalendarDays size={18} />
                                        </button>
                                        <button
                                            onClick={() => setRiepilogoUser(user)}
                                            className="text-gray-400 hover:text-emerald-600 p-1.5 rounded-md hover:bg-emerald-50 transition-colors"
                                            title="Riepilogo mensile"
                                        >
                                            <FileDown size={18} />
                                        </button>
                                        <button
                                            onClick={() => openEditModal(user)}
                                            className="text-gray-400 hover:text-blue-600 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                                            title="Modifica"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(user.id)}
                                            className="text-gray-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                                            title="Elimina"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                ),
                            },
                        ]}
                    />
                )}
            </div>

            {/* Modale agenda collaboratore */}
            {agendaUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                            <div>
                                <h2 className="text-base font-bold text-gray-900">Agenda Collaboratore</h2>
                                <p className="text-xs text-gray-500 mt-0.5">{agendaUser.name} {agendaUser.surname}</p>
                            </div>
                            <button onClick={() => setAgendaUser(null)} className="text-gray-400 hover:text-gray-600 bg-white p-1 rounded-full shadow-sm">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
                            <div className="flex flex-wrap gap-3 items-end">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Dal</label>
                                    <input type="date" value={agendaFrom} onChange={e => { setAgendaFrom(e.target.value); setAgendaData(null); }}
                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Al</label>
                                    <input type="date" value={agendaTo} onChange={e => { setAgendaTo(e.target.value); setAgendaData(null); }}
                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                                </div>
                                <button onClick={handleLoadAgenda} disabled={agendaLoading}
                                    className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2 transition-colors">
                                    {agendaLoading ? <Loader2 size={14} className="animate-spin" /> : <CalendarDays size={14} />}
                                    Carica
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                            {!agendaData && !agendaLoading && (
                                <div className="text-center py-12 text-gray-400">
                                    <CalendarDays size={40} className="mx-auto mb-3 opacity-30" />
                                    <p className="text-sm">Seleziona un intervallo e premi Carica</p>
                                </div>
                            )}
                            {agendaData && (() => {
                                const incarichi: any[] = agendaData.incarichi || [];
                                const turni: any[] = agendaData.turni || [];
                                // I 5 KPI mostrano solo valori già riconosciuti dal titolare/responsabile,
                                // cioè quanto verrà effettivamente liquidato.
                                // - Incarichi: esclusi RIFIUTATO/REVOCATO.
                                // - Relazioni: solo APPROVATO.
                                // - Ore indagini: solo relazioni APPROVATO; oreApprovateTotali se presente,
                                // altrimenti oreGps + oreExtraApprovate.
                                // - Turni: esclusi REVOCATI e RIFIUTATI.
                                // - Ore turni: oreApprovate (con override prioritario).
                                const incarichiValidi = incarichi.filter((i: any) =>
                                    i.stato !== 'RIFIUTATO' && i.stato !== 'REVOCATO');
                                const turniValidi = turni.filter((t: any) =>
                                    !t.dataRevoca && t.statoAccettazione !== 'RIFIUTATO');
                                const relazioniApprovate = incarichi.reduce(
                                    (acc: number, i: any) => acc + (i.relazioni || []).filter(
                                        (r: any) => r.stato === 'APPROVATO').length, 0);
                                // Ore "Xh Ym" arrotondate a 10 minuti (helper condiviso col PDF agenda).
                                const fmtHM = (h: number): string => {
                                    if (!h || h <= 0) return '0m';
                                    const totalMin = Math.round((h * 60) / 10) * 10;
                                    const hh = Math.floor(totalMin / 60);
                                    const mm = totalMin % 60;
                                    if (hh === 0) return `${mm}m`;
                                    if (mm === 0) return `${hh}h`;
                                    return `${hh}h ${mm}m`;
                                };
                                const totOreIndagini = incarichi.reduce((acc: number, inc: any) => {
                                    const approvate = (inc.relazioni || []).filter(
                                        (r: any) => r.stato === 'APPROVATO');
                                    return acc + approvate.reduce((a: number, r: any) => {
                                        if (r.oreApprovateTotali != null) {
                                            return a + Math.max(0, Number(r.oreApprovateTotali));
                                        }
                                        const extra = r.oreExtraApprovate != null
                                            ? Number(r.oreExtraApprovate)
                                            : Number(r.oreExtra || 0);
                                        return a + (r.oreGps || 0) + Math.max(0, extra);
                                    }, 0);
                                }, 0);
                                // Stessa logica di generaAgendaCollaboratore.oreTurno: solo turni con ore
                                // approvate; override se presente, altrimenti GPS + ore extra del report.
                                // Pianificati, in corso o da approvare valgono 0.
                                const totOreTurni = turni.reduce((acc: number, t: any) => {
                                    if (!t.oreApprovate) return acc;
                                    if (t.oreCollaboratoreOverride != null) {
                                        return acc + Math.max(0, Number(t.oreCollaboratoreOverride));
                                    }
                                    let ore = 0;
                                    if (t.effettivoInizio && t.effettivoFine) {
                                        ore += Math.max(0, (new Date(t.effettivoFine).getTime() - new Date(t.effettivoInizio).getTime()) / 3_600_000);
                                    }
                                    ore += Math.max(0, Number(t.report?.oreExtra || 0));
                                    return acc + ore;
                                }, 0);
                                // Km approvati: relazioni APPROVATO + report dei turni con ore approvate.
                                // Fallback su kmPercorsi per le relazioni senza kmApprovati.
                                const totKmApprovati = incarichi.reduce((acc: number, inc: any) => {
                                    const approvate = (inc.relazioni || []).filter((r: any) => r.stato === 'APPROVATO');
                                    return acc + approvate.reduce((a: number, r: any) =>
                                        a + Math.max(0, Number(r.kmApprovati ?? r.kmPercorsi ?? 0)), 0);
                                }, 0) + turni.reduce((acc: number, t: any) => {
                                    if (!t.oreApprovate) return acc;
                                    const km = t.report?.kmApprovati ?? t.report?.kmPercorsi ?? 0;
                                    return acc + Math.max(0, Number(km));
                                }, 0);
                                // Il colore del KPI va solo sulla barra laterale e sul valore: card bianca
                                // e label grigia per restare sobri.
                                const kpis: { label: string; value: string; bar: string; value_cls: string }[] = [
                                    { label: 'Incarichi',       value: String(incarichiValidi.length), bar: 'bg-blue-500',    value_cls: 'text-blue-700' },
                                    { label: 'Relazioni',       value: String(relazioniApprovate),     bar: 'bg-violet-500',  value_cls: 'text-violet-700' },
                                    { label: 'Ore indagini',    value: fmtHM(totOreIndagini),          bar: 'bg-rose-500',    value_cls: 'text-rose-700' },
                                    { label: 'Km percorsi',     value: `${Math.round(totKmApprovati)}`, bar: 'bg-indigo-500', value_cls: 'text-indigo-700' },
                                    { label: 'Turni sicurezza', value: String(turniValidi.length),     bar: 'bg-emerald-500', value_cls: 'text-emerald-700' },
                                    { label: 'Ore turni',       value: fmtHM(totOreTurni),             bar: 'bg-amber-500',   value_cls: 'text-amber-700' },
                                ];
                                return (
                                    <>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                                            {kpis.map(k => (
                                                <div
                                                    key={k.label}
                                                    className="relative bg-white border border-gray-200 rounded-lg pl-4 pr-3 py-3 shadow-sm overflow-hidden"
                                                >
                                                    <span className={`absolute left-0 top-0 bottom-0 w-1 ${k.bar}`} />
                                                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{k.label}</div>
                                                    <div className={`mt-1 text-2xl font-bold tabular-nums ${k.value_cls}`}>{k.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-[11px] text-gray-400 -mt-2">
                                            Valori basati sulle approvazioni del titolare/responsabile (esclusi
                                            incarichi/turni rifiutati o revocati e relazioni non ancora approvate).
                                        </p>
                                        {incarichi.length > 0 && (
                                            <div>
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Attività Investigative</h3>
                                                <div className="space-y-2">
                                                    {incarichi.map((inc: any) => {
                                                        // Riepilogo per mandato: ore e km approvati e tipo di auto (aziendale, propria
                                                        // o mista, dato che può cambiare per relazione). Se r.autoAziendale è null si usa inc.autoAziendale.
                                                        let mandOre = 0;
                                                        let mandKm = 0;
                                                        let gAzi = 0;
                                                        let gProp = 0;
                                                        for (const r of (inc.relazioni || [])) {
                                                            if (r.stato !== 'APPROVATO') continue;
                                                            if (r.oreApprovateTotali != null) {
                                                                mandOre += Math.max(0, Number(r.oreApprovateTotali));
                                                            } else {
                                                                const extra = r.oreExtraApprovate != null
                                                                    ? Number(r.oreExtraApprovate)
                                                                    : Number(r.oreExtra || 0);
                                                                mandOre += (Number(r.oreGps) || 0) + Math.max(0, extra);
                                                            }
                                                            mandKm += Math.max(0, Number(r.kmApprovati ?? r.kmPercorsi ?? 0));
                                                            const auto = r.autoAziendale != null ? !!r.autoAziendale : !!inc.autoAziendale;
                                                            if (auto) gAzi++; else gProp++;
                                                        }
                                                        const giorni = gAzi + gProp;
                                                        const autoBadge: { label: string; cls: string } | null = giorni === 0
                                                            ? null
                                                            : gAzi > 0 && gProp === 0
                                                                ? { label: 'Auto aziendale', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
                                                                : gProp > 0 && gAzi === 0
                                                                    ? { label: 'Auto privata',   cls: 'bg-slate-50 text-slate-700 border-slate-200' }
                                                                    : { label: `Auto: ${gAzi} aziendale · ${gProp} propria`, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
                                                        return (
                                                        <div key={inc.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    {/*
                                                                      Mandato + soggetto, mai il cliente: togliamo l'eventuale " - Nome Cliente"
                                                                      aggiunto dal naming automatico.
                                                                    */}
                                                                    {(() => {
                                                                        const base = (inc.praticaNome || `#${inc.id}`).split(/\s+[-–]\s+/)[0].trim();
                                                                        const titolo = inc.soggettoNome ? `${base} - ${inc.soggettoNome}` : base;
                                                                        return <div className="font-medium text-sm text-gray-900 truncate">{titolo}</div>;
                                                                    })()}
                                                                    {inc.luogo && <div className="text-xs text-gray-500 mt-0.5">{inc.luogo}</div>}
                                                                </div>
                                                                {(() => {
                                                                    // Stato come in calendario e scheda mandato: "Servizio Espletato" col report del giorno.
                                                                    const b = getIncaricoStatoBadge(inc);
                                                                    return <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0 ${b ? b.cls : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{b ? b.label : '-'}</span>;
                                                                })()}
                                                            </div>
                                                            {giorni > 0 && (
                                                                <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
                                                                    <span className="px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200">
                                                                        <span className="opacity-70">Ore approvate</span> <b>{fmtHM(mandOre)}</b>
                                                                    </span>
                                                                    {mandKm > 0 && (
                                                                        <span className="px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">
                                                                            <span className="opacity-70">Km approvati</span> <b>{Math.round(mandKm)}</b>
                                                                        </span>
                                                                    )}
                                                                    {autoBadge && (
                                                                        <span className={`px-1.5 py-0.5 rounded border ${autoBadge.cls}`}>{autoBadge.label}</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {inc.relazioni?.length > 0 && (
                                                                <div className="mt-2 space-y-1">
                                                                    {inc.relazioni.map((r: any) => {
                                                                        // Per giornata solo data, orario e stato: ore e km sono già nei chip del mandato.
                                                                        const data = r.dataRelazione ? new Date(r.dataRelazione).toLocaleDateString('it-IT') : '-';
                                                                        return (
                                                                            <div key={r.id} className="bg-white border border-gray-100 rounded-lg px-2 py-1.5 space-y-1">
                                                                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                                                                    <span className="font-medium text-gray-700 w-20 flex-shrink-0">{data}</span>
                                                                                    {r.orarioInizio && r.orarioFine && <span className="text-gray-500">{r.orarioInizio}–{r.orarioFine}</span>}
                                                                                    <span className={`ml-auto px-1.5 py-0.5 rounded text-xs ${r.stato === 'APPROVATO' ? 'bg-green-100 text-green-700' : r.stato === 'BOZZA' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{r.stato || '-'}</span>
                                                                                </div>
                                                                                {r.testoRelazione && (
                                                                                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-3 border-t border-gray-50 pt-1">{r.testoRelazione}</p>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                            {(!inc.relazioni || inc.relazioni.length === 0) && inc.sessioni?.length > 0 && (
                                                                <div className="mt-1.5 text-xs text-gray-400">{inc.sessioni.length} sessione/i registrata/e</div>
                                                            )}
                                                        </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {turni.length > 0 && (
                                            <div>
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Turni di Sicurezza</h3>
                                                <div className="space-y-2">
                                                    {turni.map((t: any) => {
                                                        const b = getTurnoStatoBadge(t);
                                                        const data = t.dataTurno ? new Date(t.dataTurno).toLocaleDateString('it-IT') : '-';
                                                        return (
                                                            <div key={t.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50 flex items-center justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    <div className="font-medium text-sm text-gray-900 truncate">{t.servizio?.nome || '-'}</div>
                                                                    <div className="text-xs text-gray-500 mt-0.5">{data} · {t.oraInizio || '?'}–{t.oraFine || '?'}{t.servizio?.clientName ? ` · ${t.servizio.clientName}` : ''}</div>
                                                                </div>
                                                                <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0 ${b ? b.cls : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{b ? b.label : '-'}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {incarichi.length === 0 && turni.length === 0 && (
                                            <div className="text-center py-8 text-gray-400">
                                                <p className="text-sm">Nessuna attività trovata nel periodo selezionato.</p>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0 flex gap-3">
                            <button type="button" onClick={() => setAgendaUser(null)}
                                className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors font-medium text-sm">
                                Chiudi
                            </button>
                            <button onClick={handleDownloadAgenda} disabled={!agendaData}
                                className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors font-medium text-sm flex justify-center items-center gap-2 shadow-sm">
                                <FileDown size={15} /> Scarica PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale riepilogo mensile */}
            {riepilogoUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50">
                            <div>
                                <h2 className="text-base font-bold text-gray-900">Riepilogo Mensile</h2>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {riepilogoUser.name} {riepilogoUser.surname}
                                </p>
                            </div>
                            <button onClick={() => setRiepilogoUser(null)} className="text-gray-400 hover:text-gray-600 bg-white p-1 rounded-full shadow-sm">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Mese di riferimento</label>
                                <input
                                    type="month"
                                    value={riepilogoMonth}
                                    onChange={e => setRiepilogoMonth(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
                                />
                            </div>
                            <p className="text-xs text-gray-400">
                                Genera un PDF con ore, km, veicolo e incarichi svolti nel mese. Nessun dato economico incluso.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setRiepilogoUser(null)}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors font-medium text-sm"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={handleDownloadRiepilogo}
                                    disabled={riepilogoLoading}
                                    className="flex-1 px-4 py-2 text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded-lg transition-colors font-medium text-sm flex justify-center items-center gap-2 shadow-sm"
                                >
                                    {riepilogoLoading
                                        ? <><Loader2 size={15} className="animate-spin" /> Caricamento...</>
                                        : <><FileDown size={15} /> Scarica PDF</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale CRUD */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100">
                        {/* Header modale */}
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">
                                    {isEditing ? 'Modifica Utente' : 'Nuovo Utente'}
                                </h2>
                                <p className="text-xs text-gray-500">
                                    {isEditing ? 'Aggiorna i dettagli esistenti' : 'Inserisci le credenziali per il nuovo accesso'}
                                </p>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors bg-white p-1 rounded-full shadow-sm hover:shadow">
                                <X size={20} />
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSave} className="p-6 space-y-5">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                                        placeholder="Mario"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Cognome</label>
                                    <input
                                        type="text"
                                        value={formData.surname}
                                        onChange={(e) => setFormData({...formData, surname: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                                        placeholder="Rossi"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                                    placeholder="mario.rossi@azienda.it"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Ruolo</label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => setFormData({...formData, role: e.target.value as UserRole})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-sm"
                                >
                                    <option value={UserRole.COLLABORATORE}>Collaboratore</option>
                                    <option value={UserRole.RESPONSABILE}>Responsabile</option>
                                    <option value={UserRole.ADMIN}>Titolare</option>
                                </select>
                            </div>

                            {!isEditing && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Password Iniziale
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                                        placeholder="••••••••"
                                        required={!isEditing}
                                        minLength={6}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        L'utente potrà cambiarla al primo accesso.
                                    </p>
                                </div>
                            )}

                            {/* Consensi legali del collaboratore (solo ADMIN, in modifica). */}
                            {isEditing && isAdmin && modalConsents && (
                                <div className="pt-4 border-t border-gray-100 mt-2">
                                    <div className="flex items-center gap-2 mb-2">
                                        <ShieldCheck size={16} className="text-blue-600" />
                                        <span className="text-sm font-bold text-gray-800">Consensi legali</span>
                                    </div>
                                    {modalConsents.pending.some((d: any) => d.mandatory) ? (
                                        <div className="mb-2 flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                            <span>
                                                Non ha ancora accettato tutti i documenti obbligatori. Da accettare:{' '}
                                                {modalConsents.pending.filter((d: any) => d.mandatory).map((d: any) => legalDocTypeLabel(d.type)).join(', ')}.
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="mb-2 flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                                            <Check size={14} className="shrink-0" /> Ha accettato tutti i documenti obbligatori.
                                        </div>
                                    )}
                                    {modalConsents.accepted.length > 0 ? (
                                        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                                            {modalConsents.accepted.map((c: any, i: number) => (
                                                <li key={`${c.documentId}-${i}`} className="px-3 py-2 flex items-center justify-between gap-3 text-xs">
                                                    <span className="text-gray-700 font-medium truncate">{legalDocTypeLabel(c.documentType)} <span className="text-gray-400">v{c.documentVersion}</span></span>
                                                    <span className="text-gray-500 shrink-0">{c.acceptedAt ? new Date(c.acceptedAt).toLocaleString('it-IT') : ''}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-xs text-gray-400">Nessun consenso registrato.</p>
                                    )}
                                </div>
                            )}

                            <div className="flex gap-3 pt-4 border-t border-gray-100 mt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors font-medium text-sm"
                                >
                                    Annulla
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-medium text-sm flex justify-center items-center gap-2 shadow-sm disabled:opacity-70 disabled:cursor-wait"
                                >
                                    {isSaving ? (
                                        <>
                                            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Salvataggio...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} /> {isEditing ? 'Aggiorna' : 'Crea Utente'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollaboratoriView;